import React, { useCallback, useMemo } from 'react';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import { Column } from 'components/display/Table/types';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { Building2, CheckCircle2, User, Users, XCircle } from 'lucide-react';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import { useNavigate } from 'react-router-dom';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  INCLUDE_TESTS_BOOLEAN_FACET,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { useSharedViewDetection } from 'shared/contexts/table-context/useSharedViewDetection';
import { clientsAPI } from 'services/swagger-api';
import { AdminClientListItem } from 'api/clients';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { formatDay } from 'core/utils/dates';

interface ClientsViewProps {
  /** Liste admin BO (toutes plateformes) : routes /admin/liste + colonnes Courtier/Plateforme. */
  admin?: boolean;
}

// customerSince = date CALENDAIRE : découpe de string, zéro fuseau
const fmtDate = (iso?: string | Date | null): string => formatDay(iso);

const fmtEuro = (amount: number): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);

/** Chips de codes (branches, produits…). */
const renderCodeChips = (codes: string[]) => {
  const list = codes || [];
  if (list.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap justify-center gap-1">
      {list.map((code) => (
        <span
          key={code}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600"
        >
          {code}
        </span>
      ))}
    </div>
  );
};

// Colonnes masquées par défaut côté courtier (réactivables via « Colonnes »)
const COURTIER_HIDDEN_COLUMNS = ['simulationCount', 'branches', 'products'];

const ClientsView: React.FC<ClientsViewProps> = ({ admin = false }) => {
  // Vue partagée : colonne Courtier si brokerName rempli par le back (détecteur données).
  const { isSharedView, detectFromItems } = useSharedViewDetection();
  const navigate = useNavigate();

  // Détail client : espace admin BO ou espace courtier selon le scope
  const detailBase = admin ? '/back-office/acteurs/clients' : '/mes-clients';
  const goToClient = useCallback(
    (id: string) => navigate(`${detailBase}/${id}`),
    [navigate, detailBase]
  );

  const columns: Column<AdminClientListItem>[] = useMemo(
    () => [
      {
        key: 'isCompany',
        label: 'Type',
        render: (value) =>
          value ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
              <Building2 className="h-4 w-4 text-purple-500" />
              Morale
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
              <User className="h-4 w-4 text-blue-500" />
              Physique
            </span>
          )
      },
      {
        key: 'name',
        label: 'Nom / Raison sociale',
        sortKey: 'name',
        render: (value, row) => (
          <button
            type="button"
            onClick={() => goToClient(row.id)}
            className="font-semibold text-primary hover:underline"
          >
            {value as string}
          </button>
        )
      },
      {
        key: 'email',
        label: 'Contact',
        render: (value, row) => (
          <div className="text-sm leading-tight text-gray-700">
            <div>{(value as string) || '—'}</div>
            {row.phone && <div className="text-xs text-gray-500">{row.phone}</div>}
          </div>
        )
      },
      {
        key: 'customerSince',
        label: 'Client depuis',
        sortKey: 'customerSince',
        render: (value) => (
          <span className="text-sm text-gray-700">{fmtDate(value as string)}</span>
        )
      },
      {
        key: 'contractCount',
        label: 'Contrats',
        sortKey: 'contractCount',
        render: (value) => <span className="text-sm text-gray-700">{value as number}</span>
      },
      {
        key: 'simulationCount',
        label: 'Simulations',
        sortKey: 'simulationCount',
        render: (value) => <span className="text-sm text-gray-700">{value as number}</span>
      },
      {
        key: 'hasActiveContract',
        label: 'Contrat en cours',
        render: (value) =>
          value ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Oui
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
              <XCircle className="h-3.5 w-3.5" />
              Non
            </span>
          )
      },
      {
        key: 'totalPremium',
        label: 'Prime TTC',
        render: (value) => (
          <span className="text-sm font-medium text-gray-900">{fmtEuro(value as number)}</span>
        )
      },
      {
        key: 'branches',
        label: 'Branches',
        render: (value) => renderCodeChips(value as string[])
      },
      {
        key: 'products',
        label: 'Produits',
        render: (value) => renderCodeChips(value as string[])
      },
      // Colonne Courtier : admin OU vue partagée (brokerName rempli par le back).
      ...(admin || isSharedView
        ? ([
            { key: 'brokerName', label: 'Courtier', render: (v) => (v as string) || '—' }
          ] as Column<AdminClientListItem>[])
        : []),
      // Colonne Plateforme : admin uniquement.
      ...(admin
        ? ([
            {
              key: 'plateformeName',
              label: 'Plateforme',
              render: (v) => (v as string) || '—'
            }
          ] as Column<AdminClientListItem>[])
        : [])
    ],
    [admin, goToClient, isSharedView]
  );

  const fetchClients = useCallback(
    async (params: any): Promise<TableResponse<AdminClientListItem>> => {
      const command = {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || undefined,
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
        filters: mapFacetFiltersToFiltersDto(params.filters)
      };
      const result = admin
        ? await clientsAPI.getAdminClients({ getGenericListCommandDto: command })
        : await clientsAPI.getClients({ getGenericListCommandDto: command });
      const items = (result.items || []) as AdminClientListItem[];
      // Vue partagée : le back remplit brokerName sur la route courtier -> colonne Courtier.
      if (!admin) {
        detectFromItems(items);
      }
      return {
        items,
        totalItems: result.totalCount || items.length,
        totalPages: Math.ceil((result.totalCount || items.length) / params.pageSize),
        currentPage: params.pageNumber
      };
    },
    [admin, detectFromItems]
  );

  const fetchClientFacets = useCallback(
    (command: FacetQueryCommand) =>
      admin
        ? clientsAPI.getAdminClientListFacets({ getGenericListCommandDto: command })
        : clientsAPI.getClientListFacets({ getGenericListCommandDto: command }),
    [admin]
  );

  return (
    <SimpleDashboardContent>
      {/* Barre de recherche globale : espace courtier uniquement, pas en admin BO */}
      {!admin && <DashboardHeader />}
      {/* Identité de page admin (le titre du tableau est porté par ce header) */}
      {admin && <GenericViewHeader title="Clients" icon={Users} section="Acteurs" />}
      <ServerTable<AdminClientListItem>
        apiConfig={{ fetchData: fetchClients }}
        columns={columns}
        title={admin ? undefined : { iconKey: 'users', label: 'Mes clients' }}
        selectable
        serverSorting
        centerColumns
        columnCustomization={{
          storageKey: admin ? 'bo-clients' : 'courtier-clients',
          defaultHidden: admin ? undefined : COURTIER_HIDDEN_COLUMNS
        }}
        facets={{ fetchFacets: fetchClientFacets, booleanFacets: INCLUDE_TESTS_BOOLEAN_FACET }}
        // Export CSV réservé à l'admin ; TODO: brancher la future route back (mêmes filtres)
        onExport={
          admin
            ? (p) =>
                exportListCsv(
                  '/api/backoffice/clients/admin/liste/csv',
                  buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
                )
            : undefined
        }
        actions={{
          row: [
            {
              label: 'seeMore',
              render: (row: AdminClientListItem, index: number) => (
                <Button key={index} color="slate" round onClick={() => goToClient(row.id)}>
                  <EyeIcon />
                </Button>
              )
            }
          ]
        }}
      />
    </SimpleDashboardContent>
  );
};

export default ClientsView;
